import React, { FC, ReactElement, useMemo } from 'react'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { SelectFilter, SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { TelSysIcon } from '../../Atoms/Icons/TelSys'
import { PartitionIcon } from '../../Atoms/Icons/Partition'
import { ALL_VALUE, ANY_VALUE_STRING_DEFAULT } from '../../../../React/Common/Constants/DefaultValues'
import { AdminFiltersSlotRenderArgs } from '../../Molecules/AdminFilters/AdminFilters'
import {
    TELEPHONY_SYSTEM_TYPE_BROADSOFT_BROADWORKS,
    TELEPHONY_SYSTEM_TYPE_BROADSOFT_M6,
    TELEPHONY_SYSTEM_TYPE_CISCO_WEBEX_CALLING,
    TELEPHONY_SYSTEM_TYPE_MS_TEAMS,
    TELEPHONY_SYSTEM_TYPE_PANASONIC_TDA_NCP,
    TELEPHONY_SYSTEM_TYPE_SIEMENS_HIPATH_3000,
    TELEPHONY_SYSTEM_TYPE_SIMULATOR,
    TelSysTypes
} from '../../../../NonReact/constants/TelephonyServerTypes'
import {
    TELSYS_LIST_LANGUAGE_KEYS,
    TELSYS_LIST_LICENSE_CATEGORY_TOKEN,
    TELSYS_MODULE_STATUS_CODE
} from './TelsysListFilterConstants'

const PLATFORM_TYPE_FILTER_SPECS: readonly { readonly id: number; readonly label: string }[] = [
    { id: TELEPHONY_SYSTEM_TYPE_SIMULATOR, label: TelSysTypes.SIMULATOR },
    { id: TELEPHONY_SYSTEM_TYPE_BROADSOFT_M6, label: TelSysTypes.BROADSOFT_M6 },
    { id: TELEPHONY_SYSTEM_TYPE_BROADSOFT_BROADWORKS, label: TelSysTypes.BROADSOFT_BROADWORKS },
    { id: TELEPHONY_SYSTEM_TYPE_SIEMENS_HIPATH_3000, label: TelSysTypes.SIEMENS_HIPATH_3000 },
    { id: TELEPHONY_SYSTEM_TYPE_PANASONIC_TDA_NCP, label: TelSysTypes.PANASONIC_TDA_NCP },
    { id: TELEPHONY_SYSTEM_TYPE_CISCO_WEBEX_CALLING, label: TelSysTypes.WEBEX_CALLING },
    { id: TELEPHONY_SYSTEM_TYPE_MS_TEAMS, label: TelSysTypes.MS_TEAMS }
]

const MODULE_STATUS_CODE_ORDER: readonly number[] = [
    TELSYS_MODULE_STATUS_CODE.UNKNOWN,
    TELSYS_MODULE_STATUS_CODE.JUST_STARTING_OR_CLOSED_DOWN,
    TELSYS_MODULE_STATUS_CODE.STOPPED,
    TELSYS_MODULE_STATUS_CODE.STARTING,
    TELSYS_MODULE_STATUS_CODE.ENDING,
    TELSYS_MODULE_STATUS_CODE.DISABLED,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_INACTIVE,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_UNLICENSED,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_STARTING_CORE,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_STARTING_DEVICE_MONITORING,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_STOPPING,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_WARNINGS,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_SYNCHRONISING,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_SYNCHRONISED,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_SYNCHRONISED_WARNINGS,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_SYNCHRONISE_FAILED,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_NOT_IN_SERVICE,
    TELSYS_MODULE_STATUS_CODE.DISABLED_COMMS_ENDED_UNGRACEFULLY,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_SYNCHRONISATION_FAILED,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_BOTS_DISABLED,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_OK_BOTS_DISABLED_WARNINGS,
    TELSYS_MODULE_STATUS_CODE.RUNNING_COMMS_INACTIVE_INCORRECT_PERMISSION
]

/**
 * Builds the language bundle key used for a telephone module status code row in {@link TelsysListLanguage}.
 *
 * @param code Numeric status code from the TelSys list API filter contract.
 * @returns Bundle key string.
 */
function getModuleStatusLanguageKey(code: number): string {
    if (code === TELSYS_MODULE_STATUS_CODE.UNKNOWN) return 'TelsysList_ModuleStatus_mNeg1'

    return `TelsysList_ModuleStatus_m${String(code)}`
}

export type TelsysListColumnFilterValues = {
    platformType: Nullable<SelectFilterOption>
    reportLicence: Nullable<SelectFilterOption>
    moduleStatus: Nullable<SelectFilterOption>
}

export type TelsysListExtraFiltersProps = AdminFiltersSlotRenderArgs & {
    language: CLanguageHandler
    values: TelsysListColumnFilterValues
    onPlatformTypeChange: (option: Nullable<SelectFilterOption>) => void
    onReportLicenceChange: (option: Nullable<SelectFilterOption>) => void
    onModuleStatusChange: (option: Nullable<SelectFilterOption>) => void
}

/**
 * Telephony-server list column filters rendered beside {@link AdminFilters} via the {@code additionalFilters} slot.
 *
 * @param props - Filter state and change handlers.
 * @returns Extra filter controls for the TelSys admin list toolbar.
 */
export const TelsysListExtraFilters: FC<TelsysListExtraFiltersProps> = ({
    language,
    isLoading,
    values,
    onPlatformTypeChange,
    onReportLicenceChange,
    onModuleStatusChange
}: TelsysListExtraFiltersProps): ReactElement => {
    const platformTypeOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
            ...PLATFORM_TYPE_FILTER_SPECS.map(
                (spec: { readonly id: number; readonly label: string }): SelectFilterOption => ({
                    label: spec.label,
                    value: spec.id
                })
            )
        ],
        []
    )

    const reportLicenceOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_HISTORIC_ONLY),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.HISTORIC_ONLY
            },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_STANDARD),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.STANDARD
            },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_ENTERPRISE),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.ENTERPRISE
            },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_ACD),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.ACD
            },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_NONE),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.NONE
            },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_MIXED),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.MIXED
            },
            {
                label: language.get(TELSYS_LIST_LANGUAGE_KEYS.LICENCE_UNKNOWN),
                value: TELSYS_LIST_LICENSE_CATEGORY_TOKEN.UNKNOWN
            }
        ],
        [language]
    )

    const moduleStatusOptions: SelectFilterOption[] = useMemo((): SelectFilterOption[] => {
        const options: SelectFilterOption[] = [
            { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
            ...MODULE_STATUS_CODE_ORDER.map(
                (code: number): SelectFilterOption => ({
                    label: language.get(getModuleStatusLanguageKey(code)),
                    value: code
                })
            )
        ]

        return options
    }, [language])

    return (
        <>
            <div>
                <SelectFilter
                    onChange={onPlatformTypeChange}
                    options={platformTypeOptions}
                    label={language.get(TELSYS_LIST_LANGUAGE_KEYS.FILTER_PLATFORM_TYPE)}
                    icon={<TelSysIcon />}
                    disabled={isLoading}
                    value={values.platformType}
                    defaultValue={ALL_VALUE}
                />
            </div>
            <div>
                <SelectFilter
                    onChange={onReportLicenceChange}
                    options={reportLicenceOptions}
                    label={language.get(TELSYS_LIST_LANGUAGE_KEYS.FILTER_REPORT_LICENCE)}
                    icon={<PartitionIcon />}
                    disabled={isLoading}
                    value={values.reportLicence}
                    defaultValue={ALL_VALUE}
                />
            </div>
            <div>
                <SelectFilter
                    onChange={onModuleStatusChange}
                    options={moduleStatusOptions}
                    label={language.get(TELSYS_LIST_LANGUAGE_KEYS.FILTER_MODULE_STATUS)}
                    icon={<TelSysIcon />}
                    disabled={isLoading}
                    value={values.moduleStatus}
                    defaultValue={ALL_VALUE}
                />
            </div>
        </>
    )
}
